import { Link } from "@tanstack/react-router";
import { Flower } from "@/components/site/Flower";
import { PROCEDURE_MEDIA } from "@/lib/procedure-media";
import { heroSizes, heroSrcSet } from "@/components/site/Hero";
import hero1280 from "@/assets/hero-1280.webp.asset.json";
import drFitzer900 from "@/assets/dr-fitzer-home-900.webp.asset.json";
import drFitzer720 from "@/assets/dr-fitzer-home-720.webp.asset.json";
import drFitzer480 from "@/assets/dr-fitzer-home-480.webp.asset.json";

/**
 * Spanish homepage hero. Mirrors the English <Hero>: full-bleed photo, H1,
 * subtitle, and the appointment plus two insurance-coverage buttons, all with
 * Spanish copy and Spanish destinations.
 */
export function EsHero() {
  const hero = PROCEDURE_MEDIA.homeHero;

  return (
    <section className="relative isolate flex min-h-[85vh] flex-col items-center justify-start overflow-hidden">
      <img
        src={hero1280.url}
        srcSet={heroSrcSet}
        sizes={heroSizes}
        width={hero.width}
        height={hero.height}
        alt={hero.alt.es}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 -z-10 h-full w-full object-cover"
      />
      <div className="flex min-h-[40vh] w-full translate-y-[30px] flex-col items-center justify-center px-6 pb-6 text-center sm:min-h-[45vh]">
        <h1 className="max-w-4xl font-serif text-2xl leading-[1.05] text-[oklch(0.18_0.02_250)] sm:text-4xl md:text-5xl">
          Cirugía bariátrica y pérdida de peso
          <br /> en el <span className="italic text-primary">norte de Virginia</span>
        </h1>
        <p className="mt-2.5 max-w-2xl text-base leading-[1.365] text-[oklch(0.18_0.02_250)]/85 md:text-lg">
          Si está considerando medicamentos para bajar de peso o un procedimiento
          endoscópico o quirúrgico, tenemos la experiencia para hacer realidad su
          meta.
        </p>
        <div className="mt-3.5 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/es/contacto"
            className="rounded-full bg-primary px-10 py-3 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 hover:opacity-90"
          >
            Pedir una cita
          </Link>
          <Link
            to="/es/seguro-para-cirugia-bariatrica"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-white shadow-lg shadow-brand-logo/20 hover:opacity-90"
          >
            Cobertura para cirugía
          </Link>
          <Link
            to="/es/cobertura-de-seguro"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-white shadow-lg shadow-brand-logo/20 hover:opacity-90"
          >
            Cobertura para medicamentos
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Portrait of Dr. Fitzer shown below the Spanish homepage hero. */
export function EsHomeDoctorFigure() {
  const doctor = PROCEDURE_MEDIA.homeDoctor;

  return (
    <figure className="relative mx-auto flex max-w-sm flex-col items-center">
      <Flower className="hidden md:block -right-16 top-0 w-40" />
      <img
        src={doctor.src}
        srcSet={`${drFitzer480.url} 480w, ${drFitzer720.url} 720w, ${drFitzer900.url} 900w`}
        sizes="(min-width: 768px) 24rem, 85vw"
        width={doctor.width}
        height={doctor.height}
        alt={doctor.alt.es}
        loading="lazy"
        decoding="async"
        className="w-full rounded-xl object-cover shadow-xl"
      />
      <figcaption className="mt-2 text-center text-sm text-muted-foreground">
        {doctor.caption.es}
      </figcaption>
    </figure>
  );
}
